import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrackedLink } from '@components/analytics/TrackedLink';
import { useAuth } from '@contexts/AuthContext';
import StartAssessmentCard from '@components/assessment/StartAssessmentCard';
import AssessmentHistoryCards from '@components/assessment/AssessmentHistoryCards';
import AutoLoginModal from '@components/modals/AutoLoginModal';
import ProfileModal from '@components/modals/ProfileModal';
import InfoCards, { defaultCards } from '@components/ui/InfoCards';
import GradientBackground from '@components/ui/GradientBackground';
import { useAutoLoginModal } from '@hooks/useAutoLoginModal';
import type { Assessment } from '../types/form.types';
import { useTheme } from '@theme/useTheme';
import { apiService } from '@services/api';
import Spinner from '@components/ui/Spinner';
import heroImage from '@assets/heroimg.png';

const Home: React.FC = () => {
  const { isAuthenticated, userToken } = useAuth();
  const { colors } = useTheme();
  const [showProfile, setShowProfile] = useState(false);
  const [latestAssessment, setLatestAssessment] = useState<Assessment | null>(null);
  const [prevAssessments, setPrevAssessments] = useState<Assessment[] | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    showModal: showAutoLoginModal,
    closeModal: closeAutoLoginModal,
    disableAutoLogin
  } = useAutoLoginModal({
    delay: 10000,
    enabled: true
  });

  useEffect(() => {
    const fetchAssessments = async () => {
      if (!userToken) {
        setLatestAssessment(null);
        setPrevAssessments(null);
        return;
      }

      setLoading(true);
      try {
        const prevResponse = await apiService.getAssessmentHistory();

        if (prevResponse.has_assessments && prevResponse.assessments && prevResponse.assessments.length > 0) {
          const assessments = prevResponse.assessments;
          setLatestAssessment(assessments[0]);
          setPrevAssessments(assessments);
        } else {
          const latestResponse = await apiService.getLatestAssessment();
          setLatestAssessment(
            latestResponse.has_assessment && latestResponse.assessment
              ? latestResponse.assessment
              : null
          );
          setPrevAssessments(null);
        }
      } catch {
        setLatestAssessment(null);
        setPrevAssessments(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [isAuthenticated, userToken]);

  useEffect(() => {
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  const handleAutoLoginModalClose = () => {
    closeAutoLoginModal();
  };

  const handleDisableAutoLogin = () => {
    disableAutoLogin();
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      <div className="relative pt-24 pb-20 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
        <GradientBackground position="top" height="medium" />
        <div className="px-6 sm:px-8 lg:px-12 xl:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="flex justify-center lg:justify-start order-2 lg:order-1 px-4 lg:px-8 lg:ml-8">
                <div className="relative w-full max-w-lg">
                  <div
                    className="absolute inset-0 rounded-tr-[3rem] rounded-bl-[3rem] md:rounded-tr-[2rem] md:rounded-bl-[2rem] md:translate-x-8 lg:rounded-tr-[6rem] lg:rounded-bl-[6rem] lg:translate-x-2 transform translate-x-2 translate-y-2 -z-10"
                    style={{ backgroundColor: colors.primary }}
                  ></div>
                  <img
                    src={heroImage}
                    alt="Sleep Health Monitoring"
                    className="relative w-full h-auto rounded-tr-[3rem] rounded-bl-[3rem] md:rounded-tr-[5rem] md:rounded-bl-[5rem] lg:rounded-tr-[6rem] lg:rounded-bl-[6rem]"
                  />
                </div>
              </div>

              <div className="text-center lg:text-left order-1 lg:order-2 px-4 lg:px-0 relative">
                <div
                  className="absolute top-0 right-0 w-32 h-32 lg:w-40 lg:h-40 rounded-2xl transform translate-x-8 -translate-y-8 -z-10"
                  style={{ backgroundColor: colors.primary }}
                ></div>

                <h1
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
                  style={{ color: colors.heading }}
                >
                  Detect Sleep Apnea Early with Our Smart App
                </h1>
                <div className="space-y-4 mb-8">
                  <p
                    className="text-lg sm:text-xl leading-relaxed font-medium"
                    style={{ color: colors.text }}
                  >
                    Sleep apnea affects millions worldwide, often going undiagnosed.
                  </p>
                  <p
                    className="text-base sm:text-lg leading-relaxed"
                    style={{ color: colors.text }}
                  >
                    Our advanced assessment tool helps you identify risk factors and get personalized recommendations for better sleep health.
                  </p>
                </div>

                <div className="flex justify-center lg:justify-start">
                  <TrackedLink
                    to="/assessment"
                    className="px-8 py-4 rounded-full font-semibold text-white text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                    style={{ backgroundColor: colors.primary }}
                    gtmId="home_hero_start_assessment"
                    gtmSection="page_home"
                    gtmLabel="Start Assessment"
                  >
                    Start Assessment
                  </TrackedLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="absolute bottom-12 left-6 md:left-32 lg:left-20 w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-tr-[2rem] rounded-bl-[2rem] md:rounded-tr-[3rem] md:rounded-bl-[3rem] lg:rounded-tr-[4rem] lg:rounded-bl-[4rem] z-10"
          style={{ backgroundColor: '#147b72' }}
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{
            duration: 0.8,
            delay: 0.5,
            ease: "easeOut"
          }}
        ></motion.div>
      </div>

      <main className="flex-1 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="xl" color={colors.primary} />
            </div>
          ) : latestAssessment ? (
            <div className="mb-12">
              <AssessmentHistoryCards assessments={prevAssessments} />
            </div>
          ) : (
            <div className="mb-12">
              <InfoCards cards={defaultCards} />
            </div>
          )}

          {!loading && (
            <div className="mb-12">
              <StartAssessmentCard
                title={!isAuthenticated || !latestAssessment
                  ? "Ready to Assess Your Sleep Health?"
                  : "Would you like to retake the assessment?"
                }
                subTitle={!isAuthenticated || !latestAssessment
                  ? "Take our comprehensive sleep apnea assessment to understand your risk factors and get personalized recommendations."
                  : "Update your information and reassess your sleep health."
                }
                buttonText={!isAuthenticated || !latestAssessment
                  ? "Start Assessment"
                  : "Retake Assessment"
                }
              />
            </div>
          )}
        </div>
      </main>

      <AutoLoginModal
        visible={showAutoLoginModal}
        onClose={handleAutoLoginModalClose}
        onDisableAutoLogin={handleDisableAutoLogin}
      />

      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default Home;
